import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';

import { graphql } from 'gatsby';
import Img from 'gatsby-image';

import ButtonLink from '../../src/components/ui/ButtonLink';
import ReactCompareImage from 'react-compare-image';
import Spinner from '../../src/components/ui/Spinner';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';
import BackToTop from '../components/BackToTop';

export const query = graphql`
  query {
    file(relativePath: { eq: "covers/light-trails.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1800) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

interface CompareWrapperProps {
  thin?: boolean;
}

const CompareWrapper = styled.div<CompareWrapperProps>`
  max-width: ${props => (props.thin ? '750px' : '100%')};
  margin: 0 auto 3rem;
`;

class RerenderCompare extends React.Component {
  t1;
  state = { delayOver: false };

  componentDidMount() {
    this.t1 = setTimeout(() => {
      this.setState({ delayOver: true });
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.t1);
  }

  render() {
    if (!this.state.delayOver) {
      return <Spinner />;
    }
    return this.props.children();
  }
}

const PageTemplate = css`
  .header-image {
    margin-bottom: 4.5rem;
    box-shadow: 0 10px 20px -16px #382757;
  }

  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }

  h2::before {
    display: none !important;
  }
`;

const LightTrails: React.FunctionComponent = ({ data }) => (
  <IndexLayout>
    <Helmet>
      <title>Free Light Trail Overlays for Photoshop | Giggster Guide</title>
      <meta
        name="description"
        content="A collection of light textures, light streaks and light trail overlay images to add creative light effects to your photographs."
      />

      <meta property="og:site_name" content="Giggster Guide" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content="Free Light Trail Overlays for Photoshop" />
      <meta
        property="og:description"
        content="A collection of light textures, light streaks and light trail overlay images to add creative light effects to your photographs."
      />
      <meta property="og:url" content="https://giggster.com/guide/light-trails/" />
      <meta property="og:image" content="https://giggster.com/guide/covers/light-trails-cover-l.jpg" />
      <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Free Light Trail Overlays for Photoshop" />
      <meta
        name="twitter:description"
        content="A collection of light textures, light streaks and light trail overlay images to add creative light effects to your photographs."
      />
      <meta name="twitter:url" content="https://giggster.com/guide/light-trails/" />
      <meta name="twitter:image" content="https://giggster.com/guide/covers/light-trails-cover-l.jpg" />
      <meta name="twitter:site" content="@Giggster" />
      <meta name="twitter:creator" content="@Giggster" />
    </Helmet>
    <Wrapper css={PageTemplate}>
      <header css={[outer, SiteHeader]}>
        <div css={inner}>
          <SiteNav />
        </div>
      </header>
      <main id="site-main" className="site-main" css={[SiteMain, outer]}>
        <article className="post page" css={[PostFull, NoImage]}>
          <PostFullHeader>
            <PostFullTitle>🔦 Free Light Trail Overlays for Photoshop</PostFullTitle>
            <h2 className="b3">Use them when you edit your photos for creative color effects</h2>
            <Img className="header-image" fluid={data.file.childImageSharp.fluid} alt="" />
            {/*<ShareButtons
              title={'Free Light Trail Overlays for Photoshop'}
              slug={'/light-trails/'}
              imgSrc={'covers/light-trails-cover-l.jpg'}
            />*/}
          </PostFullHeader>

          <PostFullContent className="post-full-content">
            <div className="post-content">
              <p>
                I like to use overlays when I edit my images to add creative color effects, and
                there's nothing better than having overlays that are real photographs instead of
                created purely digitally.
              </p>
              <p>
                I created this pack with 170 free overlays of light trails and light streaks to help
                enhance my own photographs, and decided to share it here for free.
              </p>
              <p>
                You can use them to add that extra creative touch to images. You can scroll down to
                see an example where I make use of one of these overlays. These light trails don't
                have the same look light leaks, from old film cameras, but they can sometimes
                approximate the look when tweaked a little bit.
              </p>
              <h2 className="text-center">&darr;</h2>
              <p className="text-center">
                <ButtonLink
                  href="https://purple11.s3.amazonaws.com/light-trails/purple11-light-trail-pack.zip"
                  download
                >
                  Download light trail pack
                </ButtonLink>
              </p>

              
              <hr />

              <h2>Usage Example</h2>
              <p>
                Below is an example of one of the light trails used on a dark fashion photograph.
                The light trail is subtle, but it adds just a touch of extra interesting dimension
                to the final image.
              </p>
              <CompareWrapper thin>
                <RerenderCompare>
                  {() => (
                    <ReactCompareImage
                      sliderPositionPercentage={0.5}
                      leftImage="/guide/images/light-trails/light-trail-before.jpg"
                      rightImage="/guide/images/light-trails/light-trail-after.jpg"
                      sliderLineWidth={4}
                      skeleton={<Spinner />}
                    />
                  )}
                </RerenderCompare>
              </CompareWrapper>

              <p>And here are the steps to arrive at that result:</p>
              <section className="info-box">
                <strong>1.</strong> Drag the desired light trail file into a new layer. Right-click
                and select <em>Convert to Smart Object</em> so that we don't lose quality as we
                resize the layer.
              </section>
              <figure className="text-center screenshot">
                <img src="/guide/images/light-trails/screens/light-trails04.jpg" alt="Add to new layer" />
              </figure>
              <section className="info-box">
                <strong>2.</strong> Set the blend mode to <em>Screen</em> to that the color of the
                underlining images punches through the black in the overlay. At this point you can
                also lower the opacity of the overlay layer to reduce the effect.
              </section>
              <figure className="text-center screenshot">
                <img
                  src="/guide/images/light-trails/screens/light-trails00.jpg"
                  alt="Blend Mode change"
                />
              </figure>
              <section className="info-box">
                <strong>3.</strong> You can move the overlay around to find the perfect spot for it.
                Don't be scared to also rotate it, stretch it and resize it. This is optional, but
                here I added a <em>Hue/Saturation</em> adjustment layer clipped to the overlay layer
                to increase the saturation of the overlay a little.
                <br />
                <br />
                You could also apply something like some <strong>Gaussian Blur</strong> to the
                overlay layer so that the edges are less defined.
              </section>
              <figure className="text-center screenshot">
                <img
                  src="/guide/images/light-trails/screens/light-trails02.jpg"
                  alt="Adjust saturation"
                />
              </figure>
              <section className="info-box">
                <strong>4.</strong> Finally you can use the eraser tool to remove parts of the
                overlay that don't work as well. Photoshop will warn you at this point and you'll
                have to revert back to working on a rasterized layer.
                <br />
                <br />
                That's Ok if you're sure that you won't need to resize the layer anymore. If you
                want to keep working on a Smart Object, you could instead add a layer mask and mask
                out certain areas.
              </section>
              <figure className="text-center screenshot">
                <img
                  src="/guide/images/light-trails/screens/light-trails05.jpg"
                  alt="Erasing certain sections of the overlay"
                />
              </figure>
            </div>
          </PostFullContent>
        </article>
        <MoreGoodies slug="light-trails" />
      </main>
      <BackToTop />
      <Footer />
    </Wrapper>
  </IndexLayout>
);

export default LightTrails;
